import type { CellContext } from '@tanstack/react-table';
import CellSkeleton from '@/components/Table/components/CellSkeleton';
import type { CellValue, DynamicTableRow, LinkCellValue, NumericCellValue } from '@/components/Table/types';
import { DeltaUnit } from '@/components/Table/utils/DeltaUnit';
import { InternalLinkCell } from '@/components/Table/utils/InternalLinkCell';
import { ValueWithUnit } from '@/components/Table/utils/ValueWithUnit';
import type { SeriesDefinition } from '@/types/statisticsSeriesDefs';
import { cn } from '@/utils/ui-utils';

type Props = {
  row: CellContext<DynamicTableRow, LinkCellValue | CellValue>;
  series: SeriesDefinition;
  data?: DynamicTableRow[];
};

const isLinkValue = (value: LinkCellValue | CellValue, series: SeriesDefinition): value is LinkCellValue =>
  series.isLink;

export const ColumnCell = ({ row, series, data }: Props) => {
  const isDelta = series.slug.includes(':delta');
  const value = row.getValue();

  const renderContent = () => {
    if (isLinkValue(value, series)) {
      return value.length > 0 ? <InternalLinkCell links={value} /> : <div>-</div>;
    }
    if (isDelta) {
      return (
        <DeltaUnit
          value={value as NumericCellValue}
          unit={series.unit}
          className='text-xs font-normal'
        />
      );
    }
    return (
      <ValueWithUnit
        value={value}
        className='w-full truncate'
        unit={series.unit}
      />
    );
  };

  return (
    <div className={cn('w-full min-w-0', isDelta && 'flex justify-end')}>
      {!data && <CellSkeleton variant={isDelta ? 'badge' : 'text'} />}
      {!!data && renderContent()}
    </div>
  );
};
